export interface ApiList {
  code: string;
  displayName: string | null;
  sortMode: 'date' | 'manual';
  locked: boolean;
}

export interface ApiEvent {
  id: string;
  code: string;
  title: string;
  // Datetime (local) columns:
  start_ts: string | null;  // 'YYYY-MM-DDTHH:mm:ss'
  end_ts: string | null;
  // Legacy date columns:
  start_date: string | null;
  end_date: string | null;
  location: string | null;
  url: string | null;
  notes: string | null;
  order_index: number | null;
  archived?: number | 0 | 1; // server returns 0/1
  created_at: string;
  updated_at: string;
}

const JWT_KEY = 'my-events.jwt';

export function storeToken(token: string) { localStorage.setItem(JWT_KEY, token); }
export function getToken(): string | null { return localStorage.getItem(JWT_KEY); }
export function clearToken() { localStorage.removeItem(JWT_KEY); }

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    ...init,
    headers: { 'Content-Type': 'application/json', ...(init?.headers || {}) }
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.error ? JSON.stringify(data.error) : res.statusText);
  }
  return res.json() as Promise<T>;
}

export async function apiCreateList(input: { code: string; displayName?: string; password?: string }) {
  return request<{ code: string; displayName: string | null; sortMode: 'date'|'manual' }>(`/api/lists`, {
    method: 'POST',
    body: JSON.stringify(input)
  });
}
export async function apiGetList(code: string) { return request<ApiList>(`/api/lists/${encodeURIComponent(code)}`); }

export async function apiAuthList(code: string, password: string) {
  return request<{ token: string }>(`/api/lists/${encodeURIComponent(code)}/auth`, {
    method: 'POST',
    body: JSON.stringify({ password })
  });
}

export async function apiGetEvents(code: string, opts?: { archived?: boolean }) {
  const qs = opts?.archived ? '?archived=1' : '';
  return request<{ events: ApiEvent[] }>(`/api/lists/${encodeURIComponent(code)}/events${qs}`);
}

export async function apiCreateEvent(code: string, payload: {
  title: string;
  startDate?: string; endDate?: string | null;
  startDateTime?: string; endDateTime?: string | null;
  location?: string | null; url?: string | null; notes?: string | null;
}) {
  const token = getToken();
  return request<{ event: ApiEvent }>(`/api/lists/${encodeURIComponent(code)}/events`, {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    body: JSON.stringify(payload)
  });
}

export async function apiUpdateEvent(code: string, id: string, patch: {
  title?: string;
  startDate?: string; endDate?: string | null;
  startDateTime?: string; endDateTime?: string | null;
  location?: string | null; url?: string | null; notes?: string | null;
  archived?: boolean;
}) {
  const token = getToken();
  return request<{ event: ApiEvent }>(`/api/lists/${encodeURIComponent(code)}/events/${encodeURIComponent(id)}`, {
    method: 'PUT',
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    body: JSON.stringify(patch)
  });
}

export async function apiDeleteEvent(code: string, id: string) {
  const token = getToken();
  const res = await fetch(`/api/lists/${encodeURIComponent(code)}/events/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    headers: token ? { Authorization: `Bearer ${token}` } : undefined
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.error ? JSON.stringify(data.error) : res.statusText);
  }
  return true;
}

export async function apiReorder(code: string, orderedIds: string[]) {
  const token = getToken();
  return request<{ ok: true }>(`/api/lists/${encodeURIComponent(code)}/reorder`, {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    body: JSON.stringify({ order: orderedIds })
  });
}

export async function apiUpdateListOptions(code: string, payload: { sortMode: 'date' | 'manual' }) {
  const token = getToken();
  return request<{ code: string; displayName: string | null; sortMode: 'date' | 'manual' }>(`/api/lists/${encodeURIComponent(code)}/options`, {
    method: 'PATCH',
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    body: JSON.stringify(payload)
  });
}
